import { STORAGE_KEYS } from '../constants';
import { CustomLevelManager } from './CustomLevelManager';
import type { CustomLevel } from './CustomLevelManager';

// ============================================
// Save Manager - Multiple Save Slots
// ============================================

export interface SaveSlotInfo {
  id: string;
  name: string;
  createdAt: string;
  lastPlayed: string;
  completedScenarios: number;
  totalStars: number;
}

interface SaveSlotData {
  info: SaveSlotInfo;
  progressData: object;  // The actual progress data
}

interface SaveManagerData {
  currentSlotId: string;
  slots: Record<string, SaveSlotData>;
}

const SAVE_MANAGER_KEY = 'pjm_saves';
const DEFAULT_SLOT_ID = 'auto';

class SaveManagerClass {
  private data: SaveManagerData;
  private initialized: boolean = false;

  constructor() {
    this.data = this.createDefaultData();
  }

  private createDefaultData(): SaveManagerData {
    return {
      currentSlotId: DEFAULT_SLOT_ID,
      slots: {
        [DEFAULT_SLOT_ID]: {
          info: {
            id: DEFAULT_SLOT_ID,
            name: 'Auto Save',
            createdAt: new Date().toISOString(),
            lastPlayed: new Date().toISOString(),
            completedScenarios: 0,
            totalStars: 0,
          },
          progressData: {},
        }
      }
    };
  }

  init(): void {
    if (this.initialized) return;
    this.load();
    this.initialized = true;
  }

  // ============================================
  // Persistence
  // ============================================

  private load(): void {
    try {
      const saved = localStorage.getItem(SAVE_MANAGER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.data = {
          ...this.createDefaultData(),
          ...parsed,
        };
        // Ensure auto slot exists
        if (!this.data.slots[DEFAULT_SLOT_ID]) {
          this.data.slots[DEFAULT_SLOT_ID] = {
            info: {
              id: DEFAULT_SLOT_ID,
              name: 'Auto Save',
              createdAt: new Date().toISOString(),
              lastPlayed: new Date().toISOString(),
              completedScenarios: 0,
              totalStars: 0,
            },
            progressData: {},
          };
        }
      }
    } catch (e) {
      console.error('[SaveManager] Failed to load:', e);
      this.data = this.createDefaultData();
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(SAVE_MANAGER_KEY, JSON.stringify(this.data));
      // Notify sync service to push to remote when logged in (fire-and-forget)
      import('./SaveSyncService').then(({ SaveSyncService }) => {
        SaveSyncService.schedulePush();
      }).catch(() => {});
    } catch (e) {
      console.error('[SaveManager] Failed to save:', e);
    }
  }

  /**
   * Reload state from localStorage (e.g. after applying remote save data).
   */
  reloadFromStorage(): void {
    this.load();
  }

  // ============================================
  // Slot Management
  // ============================================

  /**
   * Get current slot ID
   */
  getCurrentSlotId(): string {
    return this.data.currentSlotId;
  }

  /**
   * Get current slot name
   */
  getCurrentSlotName(): string {
    const slot = this.data.slots[this.data.currentSlotId];
    return slot?.info.name ?? 'Unknown';
  }

  /**
   * Get all save slots
   */
  getAllSlots(): SaveSlotInfo[] {
    return Object.values(this.data.slots)
      .map(slot => slot.info)
      .sort((a, b) => {
        // Auto slot always first
        if (a.id === DEFAULT_SLOT_ID) return -1;
        if (b.id === DEFAULT_SLOT_ID) return 1;
        return new Date(b.lastPlayed).getTime() - new Date(a.lastPlayed).getTime();
      });
  }

  /**
   * Create a new save slot (Save As)
   */
  createSlot(name: string): string {
    const id = `save_${Date.now()}`;
    
    // Copy current progress to new slot
    const currentSlot = this.data.slots[this.data.currentSlotId];
    
    this.data.slots[id] = {
      info: {
        id,
        name,
        createdAt: new Date().toISOString(),
        lastPlayed: new Date().toISOString(),
        completedScenarios: currentSlot?.info.completedScenarios ?? 0,
        totalStars: currentSlot?.info.totalStars ?? 0,
      },
      progressData: currentSlot ? { ...currentSlot.progressData } : {},
    };

    // Switch to new slot
    this.data.currentSlotId = id;
    this.persist();

    console.log(`[SaveManager] Created slot: ${name} (${id})`);
    return id;
  }

  /**
   * Load a save slot
   */
  loadSlot(slotId: string): boolean {
    if (!this.data.slots[slotId]) {
      console.error(`[SaveManager] Slot not found: ${slotId}`);
      return false;
    }

    this.data.currentSlotId = slotId;
    this.persist();

    console.log(`[SaveManager] Loaded slot: ${slotId}`);
    return true;
  }

  /**
   * Delete a save slot
   */
  deleteSlot(slotId: string): boolean {
    if (slotId === DEFAULT_SLOT_ID) {
      console.error('[SaveManager] Cannot delete auto save slot');
      return false;
    }

    if (!this.data.slots[slotId]) {
      return false;
    }

    delete this.data.slots[slotId];

    // If we deleted current slot, switch to auto
    if (this.data.currentSlotId === slotId) {
      this.data.currentSlotId = DEFAULT_SLOT_ID;
    }

    this.persist();
    console.log(`[SaveManager] Deleted slot: ${slotId}`);
    return true;
  }

  /**
   * Rename a save slot
   */
  renameSlot(slotId: string, newName: string): boolean {
    const slot = this.data.slots[slotId];
    if (!slot) return false;

    slot.info.name = newName;
    this.persist();
    return true;
  }

  /**
   * Start a new game (create new slot or reset auto)
   */
  startNewGame(slotName?: string): string {
    if (slotName) {
      // Create a new named slot with fresh progress
      const id = `save_${Date.now()}`;
      this.data.slots[id] = {
        info: {
          id,
          name: slotName,
          createdAt: new Date().toISOString(),
          lastPlayed: new Date().toISOString(),
          completedScenarios: 0,
          totalStars: 0,
        },
        progressData: {},
      };
      this.data.currentSlotId = id;
      this.persist();
      return id;
    } else {
      // Reset auto slot
      this.data.slots[DEFAULT_SLOT_ID] = {
        info: {
          id: DEFAULT_SLOT_ID,
          name: 'Auto Save',
          createdAt: new Date().toISOString(),
          lastPlayed: new Date().toISOString(),
          completedScenarios: 0,
          totalStars: 0,
        },
        progressData: {},
      };
      this.data.currentSlotId = DEFAULT_SLOT_ID;
      this.persist();
      return DEFAULT_SLOT_ID;
    }
  }

  // ============================================
  // Progress Integration
  // ============================================

  /**
   * Get progress data for current slot
   */
  getProgressData(): object {
    const slot = this.data.slots[this.data.currentSlotId];
    return slot?.progressData ?? {};
  }

  /**
   * Save progress data to current slot
   */
  saveProgressData(progressData: object, completedScenarios: number, totalStars: number): void {
    const slot = this.data.slots[this.data.currentSlotId];
    if (!slot) return;

    slot.progressData = progressData;
    slot.info.lastPlayed = new Date().toISOString();
    slot.info.completedScenarios = completedScenarios;
    slot.info.totalStars = totalStars;

    this.persist();
  }

  /**
   * Check if there are any saves (other than empty auto)
   */
  hasSaves(): boolean {
    const slots = this.getAllSlots();
    if (slots.length > 1) return true;

    // Check if auto slot has any progress
    const autoSlot = this.data.slots[DEFAULT_SLOT_ID];
    return autoSlot && autoSlot.info.completedScenarios > 0;
  }

  // ============================================
  // Export / Import (download / upload save)
  // ============================================

  /**
   * Export full save data as JSON string for download.
   * Includes save slots, progress, and all custom map data.
   */
  exportSaveData(): string {
    const payload = {
      ...this.data,
      customLevels: CustomLevelManager.getAllLevels(),
    };
    return JSON.stringify(payload, null, 2);
  }

  /**
   * Import save data from a previously exported JSON string.
   * Returns true if import succeeded; false if invalid.
   */
  importSaveData(jsonString: string): boolean {
    try {
      const parsed = JSON.parse(jsonString) as SaveManagerData & { customLevels?: CustomLevel[] };
      if (!parsed || typeof parsed.currentSlotId !== 'string' || !parsed.slots || typeof parsed.slots !== 'object') {
        return false;
      }
      // Ensure auto slot exists
      if (!parsed.slots[DEFAULT_SLOT_ID]) {
        parsed.slots[DEFAULT_SLOT_ID] = {
          info: {
            id: DEFAULT_SLOT_ID,
            name: 'Auto Save',
            createdAt: new Date().toISOString(),
            lastPlayed: new Date().toISOString(),
            completedScenarios: 0,
            totalStars: 0,
          },
          progressData: {},
        };
      }
      if (!parsed.slots[parsed.currentSlotId]) {
        parsed.currentSlotId = DEFAULT_SLOT_ID;
      }
      this.data = { currentSlotId: parsed.currentSlotId, slots: parsed.slots };
      this.persist();
      // Restore custom maps if present in the file (empty array = clear custom maps)
      if (Array.isArray(parsed.customLevels)) {
        CustomLevelManager.replaceAllLevels(parsed.customLevels);
      }
      console.log('[SaveManager] Imported save data');
      return true;
    } catch (e) {
      console.error('[SaveManager] Failed to import:', e);
      return false;
    }
  }
}

// Singleton
export const SaveManager = new SaveManagerClass();
